import { useCallback, useEffect, useState } from "react";

export interface ShortlistItem {
  slug: string;
  name: string;
  category?: string;
  savedAt: number;
}

const STORAGE_KEY = "tk_saved_careers_v1";
const EVENT = "tk:shortlist-changed";

function read(): ShortlistItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(items: ShortlistItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* ignore quota errors */
  }
}

export function useShortlist() {
  const [items, setItems] = useState<ShortlistItem[]>(() => read());

  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const isSaved = useCallback(
    (slug: string) => items.some((i) => i.slug === slug),
    [items],
  );

  const toggle = useCallback((item: Omit<ShortlistItem, "savedAt">) => {
    const current = read();
    const exists = current.some((i) => i.slug === item.slug);
    const next = exists
      ? current.filter((i) => i.slug !== item.slug)
      : [{ ...item, savedAt: Date.now() }, ...current];
    write(next);
    setItems(next);
    return !exists;
  }, []);

  const remove = useCallback((slug: string) => {
    const next = read().filter((i) => i.slug !== slug);
    write(next);
    setItems(next);
  }, []);

  return { items, isSaved, toggle, remove, count: items.length };
}
